import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import { Box, CircularProgress, styled, Typography } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import { formatDicomPatientName } from "../../../_lib/dicom-patient-name";
import { EXAM_ICONS, EXAM_LABELS } from "../../../_lib/epocus-exam-constants";
import {
  IDocumentsAccepted,
  IDocumentsDenied,
  UltrasoundScanner,
} from "../../../_lib/epocus-icons";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import { epocusTheme } from "../../../_lib/epocus-theme-provider";
import { linkToEpocusReview, linkToEpocusReviewPanel } from "../../../links";

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageRoot = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100%",
  backgroundColor: EPOCUS_BG,
});

const Header = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  gap: 12,
  [epocusTheme.breakpoints.up("sm")]: {
    padding: "14px 24px",
  },
});

const BackButton = styled("button")({
  width: 44,
  height: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "none",
  border: "none",
  cursor: "pointer",
  borderRadius: 10,
  color: EPOCUS_PRIMARY,
  flexShrink: 0,
  "&:hover": { backgroundColor: EPOCUS_BG },
  "&:focus-visible": {
    outline: `2px solid ${EPOCUS_PRIMARY}`,
    outlineOffset: 2,
  },
});

const HeaderTitle = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const HeaderSubtitle = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const List = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: 16,
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const ExamCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderRadius: 12,
  padding: "14px 16px",
  border: `1px solid ${EPOCUS_BORDER}`,
  display: "flex",
  alignItems: "center",
  gap: 12,
  cursor: "pointer",
  "&:hover": {
    borderColor: EPOCUS_PRIMARY,
  },
  "&:active": {
    opacity: 0.8,
  },
  "&:focus-visible": {
    outline: `2px solid ${EPOCUS_PRIMARY}`,
    outlineOffset: 2,
  },
});

const ExamIconBox = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 10,
  backgroundColor: EPOCUS_BG,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  border: `1px solid ${EPOCUS_BORDER}`,
});

const ExamInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
});

const ExamName = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const ExamDetail = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
  marginTop: 2,
});

const EmptyState = styled(Box)({
  textAlign: "center",
  padding: "48px 16px",
});

const EmptyTitle = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
  marginBottom: 6,
});

const EmptyBody = styled(Typography)({
  fontSize: rem(14),
  color: EPOCUS_TEXT_SECONDARY,
});

// ─── Exam Card ────────────────────────────────────────────────────────────────

const ExamListItem: FC<{ exam: EpocusExam }> = ({ exam }) => {
  const navigate = useNavigate();
  const Icon = EXAM_ICONS[exam.examType] ?? UltrasoundScanner;
  const label = EXAM_LABELS[exam.examType] ?? exam.examType;

  const handleClick = () => navigate(linkToEpocusReviewPanel(exam.id));

  return (
    <ExamCard
      role="button"
      tabIndex={0}
      aria-label={`Review ${label} exam`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          handleClick();
        } else if (e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <ExamIconBox>
        <Icon
          width={22}
          height={22}
          fill={EPOCUS_ICON_ACTIVE}
          aria-hidden="true"
        />
      </ExamIconBox>
      <ExamInfo>
        <ExamName>{label}</ExamName>
        <ExamDetail>
          {exam.images.length} image{exam.images.length === 1 ? "" : "s"}
        </ExamDetail>
      </ExamInfo>
      <ChevronRightIcon
        style={{ color: EPOCUS_TEXT_SECONDARY, flexShrink: 0, fontSize: 20 }}
      />
    </ExamCard>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const EpocusReviewEncounterPage: FC = () => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const navigate = useNavigate();
  const {
    replicationState: {
      epocusExam: { isPulling },
    },
  } = useRxDBContext();

  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    (collection) =>
      encounterId
        ? collection.find({ selector: { id: { $eq: encounterId } } })
        : undefined,
  );
  const encounter = encounters[0];

  const { result: exams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    (collection) =>
      encounterId
        ? collection.find({ selector: { encounterId: { $eq: encounterId } } })
        : undefined,
  );

  // Hold the loading state until the on-demand pull for this encounter has run
  // at least once. Keying off `isPulling === false` alone would flash the empty
  // state cross-device: the local query settles to [] a frame before the global
  // pull flips `isPulling` true.
  const [pullSettled, setPullSettled] = useState(false);
  const pullTracker = useRef({ encounterId, wasPulling: false });
  useEffect(() => {
    const tracker = pullTracker.current;
    if (tracker.encounterId !== encounterId) {
      // New encounter: require a fresh isPulling true→false cycle for *this*
      // encounter before settling. Carrying the previous encounter's pulling
      // state over would let its cancellation settle the new one prematurely.
      tracker.encounterId = encounterId;
      tracker.wasPulling = false;
      setPullSettled(false);
    } else if (isPulling) {
      tracker.wasPulling = true;
    } else if (tracker.wasPulling) {
      setPullSettled(true);
    }
  }, [isPulling, encounterId]);

  const loading = exams.length === 0 && !pullSettled;
  const onlyExam = exams.length === 1 ? exams[0] : undefined;

  if (pullSettled && onlyExam) {
    return <Navigate to={linkToEpocusReviewPanel(onlyExam.id)} replace />;
  }

  return (
    <PageRoot>
      <Header>
        <BackButton
          onClick={() => navigate(linkToEpocusReview())}
          aria-label="Back to review queue"
        >
          <ArrowBackIcon />
        </BackButton>
        <Box flex={1} minWidth={0}>
          <HeaderTitle>
            {formatDicomPatientName(encounter?.patientName) || "Patient"}
          </HeaderTitle>
          {encounter?.accessionNumber && (
            <HeaderSubtitle>{encounter.accessionNumber}</HeaderSubtitle>
          )}
        </Box>
      </Header>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress size={32} style={{ color: EPOCUS_PRIMARY }} />
        </Box>
      ) : exams.length > 0 ? (
        <List>
          {exams.map((exam) => (
            <ExamListItem key={exam.id} exam={exam} />
          ))}
        </List>
      ) : !encounter ? (
        <EmptyState>
          <IDocumentsDenied
            width={48}
            height={48}
            fill={EPOCUS_ICON_ACTIVE}
            aria-hidden="true"
            style={{ display: "block", margin: "0 auto 12px" }}
          />
          <EmptyTitle>Encounter not found</EmptyTitle>
          <EmptyBody>
            This encounter isn&apos;t available on this device.
          </EmptyBody>
        </EmptyState>
      ) : (
        <EmptyState>
          <IDocumentsAccepted
            width={48}
            height={48}
            fill={EPOCUS_ICON_ACTIVE}
            aria-hidden="true"
            style={{ display: "block", margin: "0 auto 12px" }}
          />
          <EmptyTitle>No exams to review</EmptyTitle>
          <EmptyBody>This encounter has no exams available yet.</EmptyBody>
        </EmptyState>
      )}
    </PageRoot>
  );
};
